import {
  UploadIcon,
  CheckCircledIcon,
  DownloadIcon,
  FileTextIcon,
} from "@radix-ui/react-icons";
import {
  Heading,
  Flex,
  Card,
  Button,
  Text,
  Badge,
  Box,
  Spinner,
} from "@radix-ui/themes";
import { Controller, useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { DragAndDropFileUpload } from "../../components/inputs/FileUpload/DragAndDropFileUpload";
import {
  FileType,
  UploadModel,
  UploadModelItemProps,
  UploadModelItemType,
} from "../../types/ModelsType";
import { Models } from "../../utils/constants";
import { getBlob, getBlobV2, postFormData } from "../../utils/axios-config";
import { useExportDocument } from "../../hooks/useExportDocument";
import SelectComponent from "../../components/inputs/Select/SelectComponent";

const DocumentModelsOptions: { value: FileType; label: string }[] = [
  { value: "CERTIFICADO_LICENCIATURA", label: "Certificado Licenciatura" },
  { value: "CARTAO_ESTUDANTE", label: "Cartão de Estudante" },
  { value: "RESET_PASSWORD", label: "Redefinição de Senha" },
  { value: "PAUTA_EXAME", label: "Pauta de exame" },
  { value: "PAUTA_FREQUENCIA", label: "Pauta de Frequência" },
  { value: "STUDENT_CLASSES", label: "Disciplinas do Estudante" },
  { value: "STUDENT_PROCESS", label: "Ficha de Matricula" },
  { value: "SUBSCRIPTION", label: "Ficha de inscrição Semestral" },
  { value: "INVOICE", label: "Factura" },
  { value: "RECEIPT", label: "Recibo" },
  { value: "STUDENT_PROCESS_REFERENCE", label: "Ficha de Matricula  com Referência" },
  { value: "SUBSCRIPTION_REFERENCE", label: "Ficha de inscrição Semestral com Referência" },
  
];

const getLabelByValue = (value: string) =>
  DocumentModelsOptions.find((opt) => opt.value === value)?.label ?? value;

const DocumentVariablesMap: Record<
  FileType,
  { label: string; description?: string }[]
> = {
  "CARTAO_ESTUDANTE": [
    { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{ies}", description: "Nome da IES" },
    { label: "{ano_lectivo}", description: "Ano letivo" },
    { label: "{nome_completo}", description: "Nome completo do estudante" },
    { label: "{curso}", description: "Curso" },
    { label: "{regime}", description: "Regime (diurno, pós-laboral, etc)" },
    { label: "{codigo}", description: "Código do estudante" },
    { label: "{nue}", description: "Número Único de Estudante" },
    { label: "{avatar}", description: "Foto do estudante" },
  ],
  "RESET_PASSWORD": [
    { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{info}", description: "Informações adicionais" },
    { label: "{nome_completo}", description: "Nome completo" },
    { label: "{documento_identificacao}", description: "BI ou Passaporte" },
    { label: "{senha}", description: "Nova senha gerada" },
    { label: "{utilizador}", description: "Nome de utilizador" },
  ],
  "STUDENT_CLASSES": [
    { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{info}", description: "Informações gerais" },
    { label: "{student_classes}", description: "Lista de disciplinas" },
    { label: "{ordem_lasses}", description: "Ordem das disciplinas" },
    { label: "{codigo}", description: "Código do estudante" },
    { label: "{nome}", description: "Primeiro nome" },
    { label: "{apelido}", description: "Apelido" },
    { label: "{cargo}", description: "Cargo (ex: estudante)" },
    { label: "{nome_completo}", description: "Nome completo" },
  ],
  "PAUTA_EXAME": [
    { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{cabecalho}", description: "Cabeçalho personalizado" },
    { label: "{Header}", description: "Cabeçalho da pauta" },
    { label: "{Footer}", description: "Rodapé da pauta" },
    { label: "{nome_completo}", description: "Nome do responsável" },
    { label: "{cargo}", description: "Cargoão do responsável" },
    { label: "{data}", description: "Data do exame" },
  ],
  "PAUTA_FREQUENCIA": [
    { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{cabecalho}", description: "Cabeçalho personalizado" },
    { label: "{Header}", description: "Cabeçalho da pauta" },
    { label: "{Footer}", description: "Rodapé da pauta" },
    { label: "{nome_completo}", description: "Nome do responsável" },
    { label: "{cargo}", description: "Cargoão do responsável" },
    { label: "{data}", description: "Data do exame" },
  ],
  "CERTIFICADO_LICENCIATURA": [
    { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{ies}", description: "Nome da IES" },
    { label: "{nome_completo}", description: "Nome completo do estudante" },
    { label: "{cargo}", description: "Cargo do reitor/diretor" },
    { label: "{estudante}", description: "Nome do estudante (redundante)" },
    { label: "{documento_de_identificacao}", description: "BI/Passaporte" },
    { label: "{pai}", description: "Nome do pai" },
    { label: "{mae}", description: "Nome da mãe" },
    { label: "{curso}", description: "Curso concluído" },
    {
      label: "{ano_lectivo_disciplina}",
      description: "Ano letivo da disciplina",
    },
    { label: "{nivel_semestre}", description: "Nível e semestre" },
    { label: "{disciplina}", description: "Nome da disciplina" },
    { label: "{resultado}", description: "Nota final" },
    { label: "{local}", description: "Local de emissão" },
    { label: "{emitido}", description: "Data de emissão" },
    { label: "{cargo}", description: "Cargo do emissor" },
  ],
  "STUDENT_PROCESS": [
    { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{ies}", description: "Nome da IES" },
    { label: "{data_emissao}", description: "Data de emissão" },
    { label: "{ano_lectivo}", description: "Ano letivo" },
    { label: "{nome_completo}", description: "Nome completo do estudante" },
    { label: "{codigo}", description: "Código do estudante" },
    { label: "{curso}", description: "Curso" },
    { label: "{local}", description: "Local de emissão" },
    { label: "{delegacao}", description: "Unidade da instituição onde o estudante está matriculado" },
    { label: "{cargo}", description: "Cargoão do responsável" },
  ],    
  "SUBSCRIPTION": [
    { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{ies}", description: "Nome da IES" },
    { label: "{data_emissao}", description: "Data de emissão" },
    { label: "{ano_lectivo}", description: "Ano letivo" },
    { label: "{nome_completo}", description: "Nome completo do estudante" },
    { label: "{codigo}", description: "Código do estudante" },
    { label: "{curso}", description: "Curso" },
    { label: "{nivel}", description: "Nível de inscrição" },
    { label: "{semestre}", description: "Semestre de inscrição" },
    { label: "{local}", description: "Local de emissão" },
    { label: "{delegacao}", description: "Unidade da instituição onde o estudante está inscrito" },
    { label: "{cargo}", description: "Cargoão do responsável" },
    {label: "{nome_disciplina}", description: "Nome da disciplina" },
    {label: "{ordem}", description: "Ordem da disciplina" },
  ],
  "INVOICE": [
  { label: "{logo}", description: "Logotipo da instituição" },
  { label: "{nr_factura}", description: "Número da factura" },
  { label: "{servico}", description: "Serviço prestado" },
  { label: "{semestre}", description: "Semestre do serviço" },
  {label: "{Servico_tabela}", description: "Descrição do serviço na tabela" },
  { label: "{data_emissao}", description: "Data de emissão da factura" },

  { label: "{ies}", description: "Nome da instituição" },
  { label: "{endereco}", description: "Endereço da instituição" },
  { label: "{nuit}", description: "NUIT da instituição" },
  { label: "{unidade}", description: "Unidade ou delegação da instituição" },
  { label: "{telefone}", description: "Telefone da instituição" },
  { label: "{email}", description: "E-mail da instituição" },

  { label: "{nome_est}", description: "Nome do estudante" },
  { label: "{codigo_est}", description: "Número ou código do estudante" },
  { label: "{nuit_est}", description: "NUIT do estudante" },
  { label: "{nr_est}", description: "Telefone do estudante" },
  { label: "{endereco_est}", description: "Endereço do estudante" },
  { label: "{email_est}", description: "E-mail do estudante" },
  { label: "{tabela}", description: "Contém Código, Designação, Quantidade, Preço Unitário, Desconto, Importância." },
  { label: "{quantidade}", description: "Quantidade faturada" },
  { label: "{preco_unitario}", description: "Preço unitário" },
  { label: "{desconto}", description: "Desconto" },
  { label: "{importancia}", description: "Importância (total do item)" },

  { label: "{descricao}", description: "Descrição adicional da factura" },
  { label: "{sub_total}", description: "Subtotal da factura" },
  { label: "{multa}", description: "Valor da multa aplicada" },
  { label: "{desconto_total}", description: "Desconto total aplicado" },
  { label: "{base_iva}", description: "Base de incidência do IVA" },
  { label: "{total_iva}", description: "Valor total do IVA" },
  {label: "{nome_departamento}", description: "Nome do departamento responsável pelo pagamento" },
  { label: "{banco}", description: "Banco para pagamento" },
  {label: "{entidade}", description: "Entidade para pagamento" },
  { label: "{referencia}", description: "Referência para pagamento" },
  { label: "{total}", description: "Valor total da factura" },
  { label: "{utilizador}", description: "Utilizador que processou o documento" },
  { label: "{data}", description: "Data de impressão do documento" }
],
"RECEIPT":[
  { label: "{logo}", description: "Logotipo da instituição" },
  { label: "{data}", description: "Data do recibo" },
  { label: "{nr_factura}", description: "Número da factura associada ao recibo" },
  { label: "{servico}", description: "Serviço pago" },
  { label: "{semestre}", description: "Semestre do serviço" },
  { label: "{ies}", description: "Nome da instituição" },
  { label: "{endereco}", description: "Endereço da instituição" },
  { label: "{nuit}", description: "NUIT da instituição" },
  { label: "{unidade}", description: "Unidade ou delegação da instituição" },
  { label: "{telefone}", description: "Telefone da instituição" },
  { label: "{email}", description: "E-mail da instituição" },
  { label: "{nome_est}", description: "Nome do estudante" },
  { label: "{codigo}", description: "Número ou código do estudante" },
  { label: "{nuit_est}", description: "NUIT do estudante" },
  { label: "{nr_est}", description: "Telefone do estudante" },
  { label: "{endereco_est}", description: "Endereço do estudante" },
  { label: "{email_est}", description: "E-mail do estudante" },
  { label: "{Num_Operacao}", description: "Número da operação de pagamento" },
  { label: "{nr_est}", description: "Número do estudante" },
  { label: "{tabela}", description: "Contém Num.Operacao, Descricao, Valor, Moeda." },
  { label: "{valor}", description: "Valor pago" },
  { label: "{moeda}", description: "Moeda do pagamento" },
  {label: "{tabela_descricao}", description: "Descrição do serviço pago" },
  { label: "{data_pagamento}", description: "Data em que o pagamento foi efectuado" },
  { label: "{total}", description: "Valor total pago" },
  { label: "{descricao_final}", description: "Descrição adicional do recibo" },
   { label: "{utilizador}", description: "Utilizador que emitiu o recibo" }

],
"STUDENT_PROCESS_REFERENCE" :[
   { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{ies}", description: "Nome da IES" },
    { label: "{data_emissao}", description: "Data de emissão" },
    { label: "{ano_lectivo}", description: "Ano letivo" },
    { label: "{nome_completo}", description: "Nome completo do estudante" },
    { label: "{codigo}", description: "Código do estudante" },
    { label: "{curso}", description: "Curso" },
    { label: "{local}", description: "Local de emissão" },
    { label: "{delegacao}", description: "Unidade da instituição onde o estudante está matriculado" },
    { label: "{cargo}", description: "Cargoão do responsável" },
    {label: "{referencia}", description: "Referência para pagamento" },
    { label: "{valor}", description: "Valor a pagar" },
    {label: "{entidade}", description: "Entidade para pagamento" },
    { label: "{banco}", description: "Banco para pagamento" },
],
"SUBSCRIPTION_REFERENCE" :[
 { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{ies}", description: "Nome da IES" },
    { label: "{data_emissao}", description: "Data de emissão" },
    { label: "{ano_lectivo}", description: "Ano letivo" },
    { label: "{nome_completo}", description: "Nome completo do estudante" },
    { label: "{codigo}", description: "Código do estudante" },
    { label: "{curso}", description: "Curso" },
    { label: "{nivel}", description: "Nível de inscrição" },
    { label: "{semestre}", description: "Semestre de inscrição" },
    { label: "{local}", description: "Local de emissão" },
    { label: "{delegacao}", description: "Unidade da instituição onde o estudante está inscrito" },
    { label: "{cargo}", description: "Cargo do responsável" },
    {label: "{nome_disciplina}", description: "Nome da disciplina" },
    {label: "{ordem}", description: "Ordem da disciplina" },
    {label: "{referencia}", description: "Referência para pagamento" },
    { label: "{valor}", description: "Valor a pagar" },
    {label: "{entidade}", description: "Entidade para pagamento" },
    { label: "{banco}", description: "Banco para pagamento" },
],
};

export const UploadModels = ({
  type = "CARTAO_ESTUDANTE",
  existingFileName,
  onUploadSuccess,
}: UploadModelItemProps) => {
  const [isUploaded, setIsUploaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { downloadOnly } = useExportDocument();

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<UploadModelItemType>({
    defaultValues: {
      type,
      file: null,
      existingFile: false,
    },
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUseNativeValidation: false,
  });

  const file = watch("file");
  const selectedType = watch("type");
  const hasFile = !!file || !!existingFileName;

  // `selectedType` entra nas dependências porque o estado "Carregado" diz
  // respeito a um documento concreto: ao trocar de tipo deixa de se aplicar.
  useEffect(() => {
    setIsUploaded(!!existingFileName);
    setValue("existingFile", !!existingFileName);
  }, [existingFileName, selectedType, setValue]);

  const handleFileSelect = (file?: File) => {
    if (!file) return;
    setValue("file", file);
    setIsUploaded(false);
  };

  const handleDownload = async () => {
    if (!selectedType ) return;
    try {
      const blob = await getBlobV2(`${Models}/${selectedType}`);
      const fileName = `${selectedType}.docx`;
      await downloadOnly(blob, fileName);
      toast.success("Download iniciado com sucesso");
    } catch (error: any) {
      console.error("Erro ao descarregar:", error);
      toast.error(error?.message || "Erro ao descarregar o ficheiro");
    }
  };

  const onSubmit = handleSubmit(async (data) => {
    if (!data.file) {
      toast.error("Por favor, selecione um arquivo");
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", data.file);
      formData.append("documentType", data.type);

      const response = await postFormData<UploadModel>(Models, formData);

      // `setIsUploaded` e não `setValue("uploaded", …)`: o `reset` logo a seguir
      // deitava fora o valor do formulário, pelo que o cartão nunca chegava a
      // mostrar o crachá "Carregado" nem o contorno verde.
      setIsUploaded(true);
      toast.success("Modelo carregado com sucesso!");

      if (onUploadSuccess && response) {
        onUploadSuccess(response);
      }

      reset({ type: data.type, file: null, existingFile: true });
    } catch (error: any) {
      console.error("Erro ao fazer upload:", error);
      toast.error(error?.message || "Erro ao carregar modelo");
    } finally {
      setIsLoading(false);
    }
  });

  const currentLabel = getLabelByValue(selectedType);
  const variables = DocumentVariablesMap[selectedType] ?? [];

  return (
    <form onSubmit={onSubmit} noValidate>
      <Flex direction="column" gap="4">
        <Card
          size="3"
          mb="3"
          style={{
            border: `2px solid ${isUploaded ? "var(--green-9)" : "var(--accent-9)"}`,
            borderRadius: "var(--radius-3)",
          }}
        >
          <Flex direction="row" gap="6" align="start">
            <Box flexGrow="1" style={{ minWidth: "300px" }}>
              <Flex direction="row" gap="4" align="start">
                {/* <Box
                  style={{
                    width: "150px",
                    height: "150px",
                    border: "2px solid var(--accent-9)",
                    borderRadius: "var(--radius-3)" ,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    backgroundColor: "var(--gray-3)",
                    padding: "10px",
                  }}
                >
                  {hasFile ? (
                    <Flex direction="column" align="center" gap="2">
                      <Box style={{ color: "var(--accent-11)", display: "flex" }}>
                        <FileTextIcon width="40" height="40" />
                      </Box>
                      <Text
                        size="1"
                        weight="medium"
                        align="center"
                        style={{ wordBreak: "break-word" }}
                      >
                        {file?.name || existingFileName || "Documento .docx"}
                      </Text>
                      <Badge color={isUploaded ? "green" : "blue"} size="1">
                        {isUploaded ? "Carregado" : "Pronto para upload"}
                      </Badge>
                    </Flex>
                  ) : (
                    <Flex direction="column" align="center" gap="2">
                      <Box style={{ color: "var(--gray-8)", display: "flex" }}>
                        <UploadIcon width="40" height="40" />
                      </Box>
                      <Text size="1" color="gray" align="center">
                        Nenhum ficheiro selecionado
                      </Text>
                    </Flex>
                  )}
                </Box> */}

                <Flex direction="column" gap="3" style={{ flex: 1 }}>
                  <Flex justify="between" align="center">
                    <Heading size="4">
                      {currentLabel}
                    </Heading>
                    {isUploaded && (
                      <Badge color="green" size="1">
                        <CheckCircledIcon /> Carregado
                      </Badge>
                    )}
                  </Flex>

                  <Flex direction="column" gap="2">
                    <SelectComponent
                      required={true}
                      name="type"
                      label="Tipo de Documento"
                      control={control}
                      errors={errors}
                      options={DocumentModelsOptions}
                      style={{ width: "100%" }}
                    />
                  </Flex>

                  <Text size="2">
                    Formato aceite: .docx
                  </Text>

                  <Flex direction="column" gap="2">
                    <Controller
                      name="file"
                      control={control}
                      rules={{
                        required: !existingFileName
                          ? "Por favor, selecione um arquivo"
                          : false,
                      }}
                      render={({ field }) => (
                        <DragAndDropFileUpload
                          onFileDrop={(file) => {
                            field.onChange(file);
                            handleFileSelect(file!);
                          }}
                          label="Arraste ou selecione o ficheiro .docx"
                          selectedFile={field.value}
                          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          disabled={isLoading}
                        />
                      )}
                    />
                    {errors.file && (
                      <Text color="red" size="1">
                        {errors.file.message}
                      </Text>
                    )}
                  </Flex>

                  <Flex gap="2" align="center" wrap="wrap">
                    <Button
                      type="submit"
                      disabled={!file || isLoading}
                    >
                      {isLoading ? <Spinner loading /> : <UploadIcon />}
                      {isLoading
                        ? "A processar..."
                        : existingFileName
                        ? "Actualizar modelo"
                        : "Carregar modelo"}
                    </Button>

                    
                {selectedType && ( 
                  <Button
                    type="button"
                    onClick={handleDownload}
                    disabled={isLoading}
                    variant="outline"
                  >
                    <DownloadIcon /> Descarregar
                  </Button>
                )}
                </Flex>

                   {existingFileName && (
                    <Text size="1" >
                      Último ficheiro: {existingFileName}
                    </Text>
                  )}
                </Flex>
              </Flex>
            </Box>

            <Box
              p="4"
              style={{
                minWidth: "320px",
                maxWidth: "380px",
                backgroundColor: "var(--gray-3)",
                borderRadius: "var(--radius-3)",
                border: "1px solid var(--gray-6)",
              }}
            >
              <Heading size="3" mb="3">
                Variáveis do Modelo
              </Heading>

              {variables.length > 0 ? (
                <Flex direction="column" gap="2">
                  {variables.map((item, idx) => (
                    <Flex
                      key={idx}
                      align="center"
                      gap="2"
                     
                    >
                      <Box
                        style={{
                          backgroundColor: "var(--accent-3)",
                          color: "var(--accent-11)",
                          padding: "var(--space-1) var(--space-2)",
                          borderRadius: "var(--radius-2)",
                          fontSize: "var(--font-size-1)",
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.label}
                      </Box>
                      <Text size="1" color="gray">
                        {item.description}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              ) : (
                <Text as="p" size="1" color="gray">
                  Seleccione um tipo de documento para ver as variáveis.
                </Text>
              )}

              <Box
                mt="4"
                p="3"
                style={{
                  backgroundColor: "var(--gray-2)",
                  borderRadius: "var(--radius-3)",
                  border: "1px solid var(--gray-6)",
                }}
              >
                <Text as="p" size="1" weight="medium">
                  <strong>Nota:</strong> Estas são as variáveis a colocar no
                  modelo que o sistema irá substituir. Use o modo desenvolvedor
                  do Word (Office) para inserir os campos.
                </Text>
              </Box>
            </Box>
          </Flex>
        </Card>
      </Flex>
    </form>
  );
};
